import React, { useState } from 'react';
import {
  Box, Chip, Drawer, IconButton, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CallDiagnostics from './CallDiagnostics';
import CallRawLogs from './CallRawLogs';
import { Call } from '../types/calls';

/**
 * Log di una chiamata in un pannello laterale, sopra la lista Chiamate.
 *
 * Prima era una pagina a sé con un proprio elenco: si arrivava alla chiamata
 * due volte, una in Chiamate e una lì. Il pannello si apre dalla riga che si
 * sta già guardando, e la lista resta visibile dietro — chiudendolo si è
 * esattamente dove si era, senza ritrovare il punto.
 */

const oreMinuti = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('it-IT', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '';

export default function CallLogDrawer({
  call, phone, open, onClose,
}: {
  call: Call | null;
  /** Telefono già mascherato come nella lista: il pannello non ne rivela di più. */
  phone: string;
  open: boolean;
  onClose: () => void;
}) {
  // La conversazione è la vista che serve quasi sempre; il grezzo si scarica
  // solo se si apre la sua scheda. La scelta resta fra una chiamata e l'altra:
  // chi sta scorrendo i log tecnici di più chiamate non deve riaprirla ogni volta.
  const [vista, setVista] = useState<'conversazione' | 'grezzo'>('conversazione');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 'min(1080px, 84vw)' },
          display: 'flex', flexDirection: 'column',
        },
      }}
      // La lista resta riconoscibile dietro, ma fuori fuoco: si vede dove si
      // tornerà chiudendo, senza che competa con il pannello.
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(15, 23, 42, 0.32)', backdropFilter: 'blur(2px)' } },
      }}
    >
      {call?.call_id && (
        <>
          <Box
            sx={{
              px: { xs: 2, md: 3 }, pt: 2, borderBottom: 1, borderColor: 'divider',
              bgcolor: 'background.paper', flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {[oreMinuti(call.start_time), phone].filter(Boolean).join(' · ')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={call.call_id}
                    title={call.call_id}
                    sx={{
                      height: 20, fontSize: '0.68rem', fontFamily: 'monospace', maxWidth: '100%',
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                  {call.motivo_chiamata && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {call.motivo_chiamata}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Tooltip title="Chiudi (Esc)">
                <IconButton onClick={onClose} aria-label="Chiudi il log della chiamata" edge="end">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Tabs
              value={vista}
              onChange={(_, v) => setVista(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ mt: 1, minHeight: 40 }}
            >
              <Tab value="conversazione" label="Conversazione"
                   sx={{ minHeight: 40, py: 0, textTransform: 'none', fontSize: '0.92rem' }} />
              <Tab value="grezzo" label="Log tecnico"
                   sx={{ minHeight: 40, py: 0, textTransform: 'none', fontSize: '0.92rem' }} />
            </Tabs>
          </Box>

          {/* Testata ferma, contenuto che scorre: le schede restano a portata
              mentre si legge una conversazione lunga. */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5 }}>
            {vista === 'conversazione'
              ? <CallDiagnostics key={call.call_id} callId={call.call_id} />
              : <CallRawLogs key={call.call_id} callId={call.call_id} />}
          </Box>
        </>
      )}
    </Drawer>
  );
}
